import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import BookingRequestPage from "./BookingRequestPage";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const BOOKINGS_API = `${API_BASE}/bookings`;
const SAILINGS_API = `${API_BASE}/sailings`;

export default function CustomerPortalBookings() {
  const { customer } = useOutletContext();
  const [bookings, setBookings] = useState([]);
  const [sailings, setSailings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editingBookingId, setEditingBookingId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [bRes, sRes] = await Promise.all([fetch(BOOKINGS_API), fetch(SAILINGS_API)]);
      const bData = await bRes.json();
      const sData = await sRes.json();
      const bookingList = Array.isArray(bData) ? bData : (bData?.data || []);
      const sailingList = Array.isArray(sData) ? sData : (sData?.data || []);

      const ownBookings = bookingList.filter(
        (b) =>
          String(b.customer) === String(customer._id) ||
          String(b.customerId) === String(customer._id) ||
          String(b.customer?._id) === String(customer._id)
      );
      ownBookings.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

      setBookings(ownBookings);
      setSailings(sailingList);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const findSailing = (booking) => {
    const ref = booking.requestedSailingRef;
    const id = booking.sailing?._id || booking.sailing;
    return sailings.find((s) => s._id === id || s.sailingRef === ref);
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm("Withdraw this booking request? This can't be undone.")) return;
    try {
      setDeletingId(bookingId);
      const res = await fetch(`${BOOKINGS_API}/${bookingId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to withdraw booking");
      setBookings((prev) => prev.filter((b) => b._id !== bookingId));
    } catch (err) {
      alert(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredBookings = bookings.filter((b) => {
    const statusOk = statusFilter === "all" || b.status === statusFilter;
    const typeOk = typeFilter === "all" || b.bookingType === typeFilter;
    return statusOk && typeOk;
  });

  const totals = {
    all: bookings.length,
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    livestockHead: bookings.reduce(
      (sum, b) => sum + (b.livestockItems || []).reduce((s, i) => s + Number(i.headCount || 0), 0),
      0
    ),
  };

  return (
    <div className="w-full p-6 space-y-6">

      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Bookings</h1>
          <p className="text-sm text-gray-500 mt-1">All booking requests you've submitted to McCallum Bros.</p>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiCard label="Total Requests" value={totals.all} />
        <KpiCard label="Pending Review" value={totals.pending} highlight={totals.pending > 0} />
        <KpiCard label="Confirmed" value={totals.confirmed} />
        <KpiCard label="Livestock Shipped" value={`${totals.livestockHead.toLocaleString()} hd`} />
      </div>

      {/* Bookings list */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Booking Requests</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Showing {filteredBookings.length} of {bookings.length}
            </p>
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
            >
              <option value="all">All types</option>
              <option value="livestock">Livestock</option>
              <option value="freight">Freight</option>
              <option value="mixed">Mixed</option>
              <option value="machinery">Machinery</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="px-5 py-12 text-center text-sm text-gray-400">Loading...</div>
        ) : filteredBookings.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-gray-400">
            {bookings.length === 0 ? "No booking requests yet." : "No requests match this filter."}
          </div>
        ) : (
          <div>
            {filteredBookings.map((b, i) => {
              const sailing = findSailing(b);
              const isOpen = expandedId === b._id;
              const livestockHead = (b.livestockItems || []).reduce((s, item) => s + Number(item.headCount || 0), 0);
              const freightWeight = (b.freightItems || []).reduce((s, item) => s + Number(item.weightKg || 0), 0);
              const freightQty = (b.freightItems || []).reduce((s, item) => s + Number(item.quantity || 0), 0);

              return (
                <div key={b._id} className={i > 0 ? "border-t border-gray-100" : ""}>
                  <button
                    onClick={() => setExpandedId(isOpen ? null : b._id)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900 text-sm">{b.bookingRef}</span>
                          <span className="text-xs text-gray-400 capitalize">· {b.bookingType}</span>
                        </div>
                        <div className="text-xs text-gray-500 truncate mt-0.5">
                          {b.requestedSailingRef ? (
                            <span className="font-medium text-gray-700">{b.requestedSailingRef}</span>
                          ) : (
                            <span className="text-gray-400">No sailing selected</span>
                          )}
                          {sailing?.etd && <span> · ETD {new Date(sailing.etd).toLocaleDateString("en-NZ", { day: "2-digit", month: "short" })}</span>}
                          {livestockHead > 0 && <span> · {livestockHead} head</span>}
                          {freightQty > 0 && <span> · Qty {freightQty}{freightWeight > 0 ? `, ${freightWeight.toLocaleString()} kg` : ""}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-3">
                      {b.customerAlert?.message && (
                        <span
                          title={b.customerAlert.message}
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                            b.customerAlert.severity === "critical" ? "bg-red-50 text-red-700" :
                            b.customerAlert.severity === "warning" ? "bg-amber-50 text-amber-700" :
                            "bg-blue-50 text-blue-700"
                          }`}
                        >
                          ⚠ Alert
                        </span>
                      )}
                      <StatusBadge status={b.status} />
                      <svg className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-5 bg-gray-50 border-t border-gray-100">

                      {/* Customer alert — shown prominently first, if set */}
                      {b.customerAlert?.message && (
                        <div className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
                          b.customerAlert.severity === "critical" ? "border-red-200 bg-red-50 text-red-800" :
                          b.customerAlert.severity === "warning" ? "border-amber-200 bg-amber-50 text-amber-800" :
                          "border-blue-200 bg-blue-50 text-blue-800"
                        }`}>
                          <div className="font-semibold">{b.customerAlert.message}</div>
                          {b.customerAlert.setAt && (
                            <div className="mt-1 text-xs opacity-70">
                              Updated {new Date(b.customerAlert.setAt).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" })}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Key facts */}
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 pt-4 pb-4">
                        <SummaryTile label="Submitted" value={b.createdAt ? new Date(b.createdAt).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }) : "—"} />
                        <SummaryTile label="Route" value={`${b.origin || "—"} → ${b.destination || "—"}`} />
                        <SummaryTile label="Sailing" value={b.requestedSailingRef || "Not yet assigned"} />
                        <SummaryTile label="ETD" value={sailing?.etd ? new Date(sailing.etd).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }) : "TBC"} />
                      </div>

                      {/* Livestock */}
                      {b.bookingType !== "freight" && (b.livestockItems || []).length > 0 && (
                        <div className="mb-4">
                          <div className="flex items-center gap-1.5 mb-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Livestock</div>
                          </div>
                          <div className="space-y-2">
                            {b.livestockItems.map((item, idx) => (
                              <div key={idx} className="rounded-lg border border-gray-200 bg-white px-4 py-3 flex items-center justify-between text-sm">
                                <div>
                                  <span className="font-medium text-gray-900 capitalize">{item.livestockType}</span>
                                  {item.animalClass && <span className="text-gray-400 capitalize"> · {item.animalClass}</span>}
                                </div>
                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                  <span><span className="font-semibold text-gray-900">{item.headCount}</span> head</span>
                                  {item.averageWeightKg ? <span>{item.averageWeightKg} kg avg</span> : null}
                                  {item.fitForTransport && <span className="text-emerald-600 font-medium">✓ Fit for transport</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Freight */}
                      {(b.bookingType === "freight" || b.bookingType === "mixed") && (b.freightItems || []).length > 0 && (
                        <div className="mb-4">
                          <div className="flex items-center gap-1.5 mb-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Freight</div>
                          </div>
                          <div className="space-y-2">
                            {b.freightItems.map((item, idx) => (
                              <div key={idx} className="rounded-lg border border-gray-200 bg-white px-4 py-3 flex items-center justify-between text-sm">
                                <div>
                                  <span className="font-medium text-gray-900">{item.description || item.freightType}</span>
                                  {item.freightType && item.description && <span className="text-gray-400 capitalize"> · {item.freightType}</span>}
                                </div>
                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                  {item.quantity ? <span>Qty {item.quantity}</span> : null}
                                  {item.weightKg ? <span>{item.weightKg.toLocaleString()} kg</span> : null}
                                  {item.cubeM3 ? <span>{item.cubeM3} m³</span> : null}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {b.notes && (
                        <div className="mb-4">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Notes</div>
                          <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">{b.notes}</div>
                        </div>
                      )}

                      {/* Invoice */}
                      {b.invoice?.invoiceStatus && b.invoice.invoiceStatus !== "Not Invoiced" && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-center justify-between text-sm mb-4">
                          <span className="font-medium text-blue-800">Invoice: {b.invoice.invoiceStatus}</span>
                          {b.invoice.amountDue != null && (
                            <span className="text-blue-700">Amount due: ${Number(b.invoice.amountDue).toLocaleString()}</span>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2 border-t border-gray-200 mt-1 pt-3">
                        <a
                          href={`/portal/bookings/${b._id}/print`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          Print
                        </a>

                        {b.status === "pending" && (
                          <>
                            <button
                              onClick={() => setEditingBookingId(b._id)}
                              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                            >
                              Edit Request
                            </button>
                            <button
                              onClick={() => deleteBooking(b._id)}
                              disabled={deletingId === b._id}
                              className="rounded-lg border border-red-200 bg-white px-4 py-2 text-xs font-semibold text-red-500 hover:bg-red-50 disabled:opacity-50"
                            >
                              {deletingId === b._id ? "Withdrawing..." : "Withdraw Request"}
                            </button>
                          </>
                        )}
                        </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit modal-ish panel */}
      {editingBookingId && (
        <EditBookingPanel
          bookingId={editingBookingId}
          booking={bookings.find((b) => b._id === editingBookingId)}
          customerId={customer._id}
          onClose={() => setEditingBookingId(null)}
          onSaved={() => {
            loadData();
            setEditingBookingId(null);
          }}
        />
      )}
    </div>
  );
}

function EditBookingPanel({ bookingId, booking, customerId, onClose, onSaved }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 bg-amber-50 border-b border-amber-100">
        <span className="text-sm font-semibold text-amber-800">Editing booking request</span>
        <button onClick={onClose} className="text-xs font-semibold text-amber-700 hover:underline">
          Cancel
        </button>
      </div>
      <div className="p-6">
        <BookingRequestPage
          embedded
          fixedCustomerId={customerId}
          editBookingId={bookingId}
          initialData={booking}
          onSubmitted={onSaved}
        />
      </div>
    </div>
  );
}

function KpiCard({ label, value, highlight }) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? "border-amber-200 bg-amber-50" : "border-gray-200 bg-white"}`}>
      <div className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${highlight ? "text-amber-600" : "text-gray-400"}`}>{label}</div>
      <div className={`text-xl font-bold tracking-tight ${highlight ? "text-amber-900" : "text-gray-900"}`}>{value}</div>
    </div>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
      <div className="text-xs text-gray-400 mb-0.5">{label}</div>
      <div className="text-sm font-semibold text-gray-900 truncate">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = String(status || "pending").toLowerCase();
  const styles =
    s === "confirmed" ? "bg-emerald-50 text-emerald-700" :
    s === "cancelled" ? "bg-red-50 text-red-700" :
    "bg-amber-50 text-amber-700";
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium capitalize ${styles}`}>{s}</span>;
}